import { useState, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, X, Plus, RefreshCw, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { DEFAULT_FLOWER_SIZE, effectsForProduct, effectEmoji } from '../utils/flowerSizes';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

// Mirrors backend strain_ai.MOOD_CATALOG. Frontend has its own copy so the
// modal renders instantly without a network round-trip on first open.
const MOODS = [
  { key: 'relax',    label: 'RELAX',    emoji: '😌' },
  { key: 'energy',   label: 'ENERGY',   emoji: '⚡' },
  { key: 'focus',    label: 'FOCUS',    emoji: '🎯' },
  { key: 'sleep',    label: 'SLEEP',    emoji: '🌙' },
  { key: 'social',   label: 'SOCIAL',   emoji: '🎉' },
  { key: 'creative', label: 'CREATIVE', emoji: '🎨' },
];

// Pink accent reserved for this premium feature; rest of the app stays green.
const PINK = '#B400FF';

export function StrainFinderModal({ open, onClose }) {
  const { addToCart } = useCart();
  const [mood, setMood] = useState(null);
  const [optionalText, setOptionalText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {match, blurb, alternatives}
  const [excludeIds, setExcludeIds] = useState([]);
  const [error, setError] = useState(null);

  const reset = useCallback(() => {
    setMood(null);
    setOptionalText('');
    setResult(null);
    setExcludeIds([]);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const runMatch = useCallback(async (excludeOverride) => {
    if (!mood) return;
    setLoading(true);
    setError(null);
    try {
      const body = { mood, optional_text: optionalText || null };
      const exclude = excludeOverride || excludeIds;
      if (exclude.length > 0) body.exclude_ids = exclude;
      const { data } = await axios.post(`${API_URL}/api/strain-finder/match`, body, {
        withCredentials: true,
      });
      if (!data.match) {
        setError("We couldn't find a flower match in stock right now. Check back soon.");
        setResult(null);
      } else {
        setResult(data);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  }, [mood, optionalText, excludeIds]);

  const handleMatchAgain = useCallback(() => {
    if (!result?.match) return;
    const next = [...excludeIds, result.match.id];
    setExcludeIds(next);
    runMatch(next);
  }, [result, excludeIds, runMatch]);

  const handleAddToCart = useCallback(() => {
    if (!result?.match) return;
    addToCart(result.match, 1, {
      size: DEFAULT_FLOWER_SIZE.key,
      eighths: DEFAULT_FLOWER_SIZE.eighths,
      unitPrice: DEFAULT_FLOWER_SIZE.price,
    });
    handleClose();
  }, [result, addToCart, handleClose]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        className="max-w-md p-0 bg-[#0C0612] border-2 rounded-2xl overflow-hidden"
        style={{ borderColor: PINK, boxShadow: `0 0 32px ${PINK}40` }}
        data-testid="strain-finder-modal"
      >
        <div className="p-6 space-y-5 max-h-[85vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-start gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(180,0,255,0.12)' }}
            >
              <Sparkles className="w-6 h-6" style={{ color: '#FFB000' }} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-white font-bold text-2xl tracking-tight leading-none uppercase font-sans">
                Strain Finder
              </h2>
              <p className="text-[#7A6B73] text-[10px] uppercase tracking-[0.25em] mt-1">
                Quick Recommendations
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-1 text-[#7A6B73] hover:text-white"
              data-testid="strain-finder-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {!result && (
            <>
              <p className="text-[#A3A0A8] text-xs uppercase tracking-wider leading-relaxed">
                Pick a mood for a quick match.
              </p>

              {/* Mood chips — 2 col grid */}
              <div className="grid grid-cols-2 gap-2.5" data-testid="strain-finder-moods">
                {MOODS.map((m) => {
                  const active = mood === m.key;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => setMood(m.key)}
                      data-testid={`strain-finder-mood-${m.key}`}
                      className="flex items-center gap-2 px-4 py-3 rounded-full border transition-all text-sm font-bold tracking-wide"
                      style={{
                        background: active ? 'rgba(180,0,255,0.12)' : '#171318',
                        borderColor: active ? PINK : '#2A2329',
                        color: active ? '#FFFFFF' : '#A3A0A8',
                      }}
                    >
                      <span aria-hidden="true">{m.emoji}</span>
                      <span>{m.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Optional text */}
              <input
                type="text"
                value={optionalText}
                onChange={(e) => setOptionalText(e.target.value)}
                placeholder="Optional: focus, relax, or creative…"
                className="w-full px-4 py-3 rounded-xl bg-[#171318] border border-[#2A2329] text-white text-sm placeholder:text-[#5C525B] focus:outline-none focus:border-[#B400FF]/50"
                data-testid="strain-finder-optional"
                maxLength={120}
              />

              {/* Find button */}
              <Button
                onClick={() => runMatch()}
                disabled={!mood || loading}
                data-testid="strain-finder-submit"
                className="w-full py-6 rounded-xl text-base font-extrabold tracking-wider uppercase"
                style={{
                  background: !mood || loading ? '#3D3640' : '#FFFFFF',
                  color: !mood || loading ? '#7A6B73' : '#171318',
                }}
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> FINDING…</>
                ) : (
                  <>FIND MY STRAIN <Sparkles className="w-5 h-5 ml-2" style={{ color: '#FFB000' }} /></>
                )}
              </Button>

              {error && (
                <p className="text-[#B400FF] text-sm font-mono" data-testid="strain-finder-error">{error}</p>
              )}
            </>
          )}

          {/* Result panel */}
          {result?.match && (
            <ResultPanel
              match={result.match}
              blurb={result.blurb}
              loading={loading}
              onAddToCart={handleAddToCart}
              onMatchAgain={handleMatchAgain}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResultPanel({ match, blurb, loading, onAddToCart, onMatchAgain }) {
  const effects = effectsForProduct(match);
  return (
    <div className="space-y-5" data-testid="strain-finder-result">
      {/* Blurb callout */}
      <div
        className="rounded-xl p-4 border-l-4"
        style={{
          background: 'rgba(255,176,0,0.06)',
          borderLeftColor: '#FFB000',
        }}
      >
        <p className="text-white text-base leading-relaxed whitespace-pre-line" data-testid="strain-finder-blurb">
          “{blurb}”
        </p>
      </div>

      {/* Strain card */}
      <div className="rounded-2xl border border-[#2A2329] bg-[#0F0B11] overflow-hidden">
        <div className="flex gap-4 p-4">
          <div className="w-28 h-28 rounded-xl overflow-hidden flex-shrink-0 border border-[#2A2329]">
            {match.image_url ? (
              <img src={match.image_url} alt={match.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-[#171318] flex items-center justify-center">
                <Sparkles className="w-8 h-8" style={{ color: PINK }} />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 space-y-1.5">
            {match.strain_type && (
              <p
                className="text-[10px] font-bold uppercase tracking-[0.2em]"
                style={{ color: PINK }}
              >
                {match.strain_type}
              </p>
            )}
            <h3 className="text-white font-extrabold text-2xl uppercase leading-tight tracking-tight">
              {match.name}
            </h3>
            <p className="text-[#A3A0A8] text-sm leading-snug whitespace-pre-line line-clamp-3">
              {match.vibe_description || match.description}
            </p>
          </div>
        </div>

        {effects.length > 0 && (
          <div className="px-4 pb-3 flex flex-wrap gap-1.5">
            {effects.map((e) => (
              <span
                key={e}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#171318] border border-[#2A2329] text-[#D4D0D8] text-xs"
              >
                <span aria-hidden="true">{effectEmoji(e)}</span>
                {e}
              </span>
            ))}
          </div>
        )}

        <div className="px-4 pb-4">
          <p className="font-extrabold text-2xl mb-3" style={{ color: PINK }}>
            ${DEFAULT_FLOWER_SIZE.price}
          </p>
          <div className="flex gap-2">
            <Button
              onClick={onAddToCart}
              data-testid="strain-finder-add-to-cart"
              className="flex-1 py-5 rounded-xl bg-white text-[#171318] hover:bg-[#F2EFF3] text-sm font-extrabold uppercase tracking-wider"
            >
              ADD TO CART <Plus className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={onMatchAgain}
              disabled={loading}
              data-testid="strain-finder-match-again"
              variant="outline"
              className="flex-1 py-5 rounded-xl bg-transparent border-[#2A2329] text-white hover:bg-[#171318] hover:text-white text-sm font-extrabold uppercase tracking-wider"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>MATCH AGAIN <RefreshCw className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
